import type { RefObject } from 'react'

import { getVisitorTokenInfo, identify } from './identify'

const SCRIPT_ID = 'hubspot-messages-loader'
const SCRIPT_SRC = 'https://js.usemessages.com/conversations-embed.js'
const CHAT_CONFIG = {
  'data-loader': 'hs-scriptloader',
  'data-hsjs-portal': '4960096',
  'data-hsjs-env': 'prod',
}

export const CHAT_VALID_ISO_CODES = [
  'es',
  'it',
  'fr',
  'pt',
  'us',
  'mx',
  'br',
  'de',
  'ca',
  'gb',
  'ie',
]

type ChatAttribute = keyof typeof CHAT_CONFIG

type HubspotChatConfig = {
  email: string
  token: string | null
  refElementId: string
}

const chatbotSettings = ({ email, token, refElementId }: HubspotChatConfig) => {
  return {
    loadImmediately: false,
    inlineEmbedSelector: `#${refElementId}`,
    enableWidgetCookieBanner: true,
    disableAttachment: true,
    identificationEmail: email,
    ...(token && { identificationToken: token }),
  }
}

const loadHubspotChatScript = async (chatConfig: HubspotChatConfig) => {
  window.hsConversationsSettings = chatbotSettings(chatConfig)

  const script = document.createElement('script')
  script.src = SCRIPT_SRC
  script.type = 'text/javascript'
  script.id = SCRIPT_ID
  for (const configEntry of Object.keys(CHAT_CONFIG)) {
    script.setAttribute(configEntry, CHAT_CONFIG[configEntry as ChatAttribute])
  }
  const i = document.getElementsByTagName('script')[0]
  i?.parentNode?.insertBefore(script, i)

  window.hsConversationsOnReady = [
    () => {
      const widget = window.HubSpotConversations?.widget
      widget.load()
    },
  ]
}

const extractVisitorToken = async (email: string): Promise<string | null> => {
  const tokenInfo = getVisitorTokenInfo()

  if (tokenInfo.token && !tokenInfo.isExpired) return tokenInfo.token

  const token = await identify(email)

  return token
}

export const loadChat = async (chatContainerRef: RefObject<HTMLDivElement>, email: string) => {
  const chatElement = chatContainerRef?.current

  if (!chatElement) return

  const token = await extractVisitorToken(email)

  await loadHubspotChatScript({ email, token, refElementId: chatContainerRef.current.id })
}
